<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import * as events from '@/events';

import DeviceListItem from '@/components/device/list/DeviceListItem.vue';
import DeviceListBulkActions from '@/components/device/list/DeviceListBulkActions.vue';
import CommonSearchBarSafeArea from '@/components/_common/CommonSearchBarSafeArea.vue';
import CommonListGrayArea from '@/components/_common/CommonListGrayArea.vue';
import VLink from '@/components/ui/VLink.vue';

import { setTitle } from '@/utils/title';
import { formatTime } from '@/filters';
import { useSetItemIdOnDynamicList } from '@/use/use-set-item-id-on-recycle-list';
import { useDevice } from '@/composables/useDevice.ts';
import { usePosition } from '@/composables/usePosition.ts';

const store = useStore();
const route = useRoute();
const account = store.getters['auth/account'];
const queryClient = useQueryClient();

setTitle('All Trackers');

const props = defineProps<{
  deviceIds: any[];
}>();

const deviceStore = useDevice();
const { selectedIds, devices } = storeToRefs(deviceStore);
const { setSelectedIds } = deviceStore;

const { getPositionByDeviceId } = usePosition();

const b2bStatuses = 'cancelled';
const defaultStatuses = 'active,pending,pending-renewal,pending-cancellation';

const { editItemId, setEditItemId, resetEditItemId } =
  useSetItemIdOnDynamicList('device');

const isSelectedAll = ref(false);

const computedSelectedIds = computed({
  get() {
    return selectedIds.value;
  },
  set(ids) {
    setSelectedIds(ids);
  },
});

onMounted(() => {
  if (selectedIds.value.length === props.deviceIds.length) {
    isSelectedAll.value = true;
  }

  events.trigger(events.names.ALL_TRACKERS_VIEWED);
});

const isIndeterminate = computed(() => {
  return (
    selectedIds.value.length > 0 &&
    selectedIds.value.length < props.deviceIds.length
  );
});

function selectAll(checked: boolean) {
  if (checked) {
    setSelectedIds(props.deviceIds);
  } else {
    setSelectedIds([]);
  }
}

function subscriptionStatus(device) {
  return device?.subscription_status || device?.subscription?.status;
}

function isEnabled(device) {
  const statuses = account.is_b2b_account
    ? `${defaultStatuses},${b2bStatuses}`
    : defaultStatuses;

  return statuses.includes(subscriptionStatus(device));
}

const items = computed(() => {
  const result: any[] = [];
  props.deviceIds.forEach((id) => {
    const device = devices.value.get(id);
    if (!device) {
      return;
    }
    // Create a copy to avoid mutating readonly objects from vue-query
    const deviceCopy = { ...device };
    deviceCopy.position = getPositionByDeviceId(id);
    result.push(deviceCopy);
  });
  return result;
});

const isDeviceListView = computed(() => route.name === 'DeviceListView');

const selectedDevicesHaveAnyNotHidden = computed(() => {
  return selectedIds.value.some((id) => {
    const d = devices.value.get(id);
    return d && d.status !== 'hidden';
  });
});

const selectedDevicesAllHidden = computed(() => {
  if (selectedIds.value.length === 0) return false;
  return selectedIds.value.every((id) => {
    const d = devices.value.get(id);
    return d?.status === 'hidden';
  });
});

function openBulkArchiveModal() {
  const accountId = store.getters['auth/accountId'];
  const ids = selectedIds.value;
  const idsToArchive = ids.filter((id) => {
    const d = devices.value.get(id);
    return d && d.status !== 'hidden';
  });
  if (idsToArchive.length === 0) return;
  store.dispatch('modal/open', {
    name: 'DeviceArchiveConfirmModal',
    params: {
      title: `Archive ${idsToArchive.length} Device${idsToArchive.length === 1 ? '' : 's'}?`,
      onConfirm: () =>
        deviceStore
          .updateDevicesVisibility(accountId, idsToArchive, 'hidden')
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['account-devices'] });
            setSelectedIds([]);
          }),
      successMessage: 'Trackers archived',
      errorMessage: 'Failed to archive',
    },
  });
}

function exportSelected() {
  const accountId = store.getters['auth/accountId'];
  const ids = selectedIds.value;
  if (ids.length === 0) return;

  events.trigger(events.names.TRACKERS_EXPORTED);
  deviceStore.exportDeviceList(
    accountId,
    ids,
    `trackers_${formatTime(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`,
  );
}

function openBulkUnarchiveModal() {
  const accountId = store.getters['auth/accountId'];
  const ids = selectedIds.value;
  store.dispatch('modal/open', {
    name: 'BulkUnarchiveConfirmModal',
    params: {
      accountId,
      deviceIds: ids,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['account-devices'] });
        setSelectedIds([]);
      },
    },
  });
}
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <CommonListGrayArea>
      <UICheckboxNew
        id="select_all_trackers"
        class="mr-4"
        :checked="
          isIndeterminate || selectedIds.length === props.deviceIds.length
        "
        :indeterminate="isIndeterminate"
        @change="selectAll($event.target.checked)"
      />
      <label
        for="select_all_trackers"
        class="cursor-pointer text-[11px] font-semibold uppercase leading-5 text-gpx_slate-600"
      >
        Select All
      </label>
    </CommonListGrayArea>
    <DeviceListBulkActions
      v-if="isDeviceListView && selectedIds.length > 0"
      :count="selectedIds.length"
      :show-archive="selectedDevicesHaveAnyNotHidden"
      :show-unarchive="selectedDevicesAllHidden"
      @archive="openBulkArchiveModal"
      @unarchive="openBulkUnarchiveModal"
      @export="exportSelected"
      @clear="setSelectedIds([])"
    />
    <DynamicScroller
      class="min-h-0 flex-1"
      :items="items"
      :min-item-size="98"
      :emit-update="true"
      key-field="id"
      listClass="ds-device-list"
      itemClass="ds-device-list-item"
    >
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[
            item.name,
            subscriptionStatus(item),
            item.note,
            item?.position?.address,
          ]"
          :data-index="index"
        >
          <div class="border-b border-gpx_gray-900">
            <div
              v-if="!isEnabled(item)"
              class="flex py-[15px] pl-6 pr-[48px] md:pl-12 md:pr-[56px]"
            >
              <div @click.stop="" class="mr-4 flex pt-[3px]">
                <UICheckboxNewVModel
                  :value="item.id"
                  v-model="computedSelectedIds"
                />
              </div>
              <DeviceListItem
                class="w-full overflow-hidden"
                :device="item"
                :edit-item-id="editItemId"
                @setEditItemId="setEditItemId"
                @resetEditItemId="resetEditItemId"
              />
            </div>
            <VLink
              v-else
              block
              :to="`/trackers/${item.id}`"
              class="bg-white hover:bg-gpx_gray-900"
            >
              <div class="flex py-[15px] pl-6 pr-[48px] md:pl-12 md:pr-[56px]">
                <div @click.stop="" class="mr-4 flex pt-[3px]">
                  <UICheckboxNewVModel
                    :value="item.id"
                    v-model="computedSelectedIds"
                  />
                </div>
                <DeviceListItem
                  class="w-full overflow-hidden"
                  :device="item"
                  :edit-item-id="editItemId"
                  @setEditItemId="setEditItemId"
                  @resetEditItemId="resetEditItemId"
                />
              </div>
            </VLink>
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
  </div>
</template>

<style>
.ds-device-list {
  @apply !overflow-visible;
}
</style>
